import { RootState, useAppStore } from 'modules/Editor/store';
import {
  selectGroupWidgetFromMemberWidgetId,
  selectGroupWidgets,
  selectWidget,
} from 'modules/Editor/store/infographSelector';
import { useCallback } from 'react';
import { WrapperPos } from 'types/flowCore.types';
import { WidgetType } from 'types/widget.types';
import { getElementForWidgetId, getWidgetRotation } from 'utils/dom';
import { convertWrapperPosToRect, getEndPos, getStartPos } from 'widgets/LineWidget/LineWidget.helpers';
import { PortData, Pos, Rect, Side } from 'widgets/LineWidget/LineWidget.types';
import {
  convertRectPosToNewOriginPos,
  generateDefaultStartEndSides,
  getLineDirectionOfSideWithRotation,
} from 'widgets/LineWidget/StepsLineWidget/StepsLineWidget.helpers';
import { generateBoundingRectFromWidget, getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { GroupWidgetData } from 'widgets/Widget.types';

interface LineWidgetDataConnectProps {
  startPort: PortData | null | undefined;
  endPort: PortData | null | undefined;
  leftPx: number;
  topPx: number;
  posList: Pos[];
}
/**
 * Hook to get the realtime Rect dimensions of the widgets attached to a line widget
 * in case it is being dragged / resized.
 * If the dimensions are not changing, then it will get the dimensions from the store.
 *
 * @param lineWidget - The data related to the LineWidget.
 * @param realTimeStartRect - The realtime dimensions of the starting rectangle.
 * @param realTimeEndRect - The realtime dimensions of the ending rectangle.
 *
 * @returns {{
 *  relativeStartRect: Rect,    // The relative position and dimensions of the start rectangle.
 *  relativeEndRect: Rect,      // The relative position and dimensions of the end rectangle.
 *  startSide: Side,            // The side of the starting port.
 *  endSide: Side               // The side of the ending port.
 * }}
 */
export const useCurrentLineData = (
  lineWidget: LineWidgetDataConnectProps | null,
  realTimeStartRect?: Rect | null,
  realTimeEndRect?: Rect | null,
  realTimeStartPort?: PortData | null,
  realTimeEndPort?: PortData | null,
  isUseRealTimeStartRect?: boolean,
  isUseRealTimeEndRect?: boolean,
) => {
  const store = useAppStore();
  const storeState = store.getState() as RootState;
  const { getRectOfGroupFromWidgetIdAsRect } = useGetWidgetDataRect();

  if (lineWidget === null) {
    return {
      relativeStartRect: { x: 0, y: 0, width: 0, height: 0 },
      relativeEndRect: { x: 0, y: 0, width: 0, height: 0 },
      startSide: Side.NORTH,
      endSide: Side.SOUTH,
    };
  }

  const { startPort, endPort, leftPx, topPx, posList } = lineWidget;
  const lastPos = getEndPos(posList);
  const firstPos = getStartPos(posList);
  const startWidgetData = !!startPort
    ? selectWidget(startPort?.widgetId)(storeState)
    : !!realTimeStartPort
    ? selectWidget(realTimeStartPort?.widgetId)(storeState)
    : null;

  const endWidgetData = !!endPort
    ? selectWidget(endPort?.widgetId)(storeState)
    : !!realTimeEndPort
    ? selectWidget(realTimeEndPort?.widgetId)(storeState)
    : null;
  const isStartGroupWidget = !!startPort && getWidgetTypeFromId(startPort.widgetId) === WidgetType.Group;
  const isEndGroupWidget = !!endPort && getWidgetTypeFromId(endPort.widgetId) === WidgetType.Group;

  const startRect =
    !!realTimeStartRect || isUseRealTimeStartRect
      ? realTimeStartRect
      : isStartGroupWidget
      ? getRectOfGroupFromWidgetIdAsRect(startPort.widgetId)
      : generateBoundingRectFromWidget(startWidgetData);
  const endRect =
    !!realTimeEndRect || isUseRealTimeEndRect
      ? realTimeEndRect
      : isEndGroupWidget
      ? getRectOfGroupFromWidgetIdAsRect(endPort.widgetId)
      : generateBoundingRectFromWidget(endWidgetData);

  // This is the rect of the widget relative to the startPos of the line widget.
  const relativeStartRect =
    startRect && !!startWidgetData
      ? convertRectPosToNewOriginPos(startRect, { xPx: leftPx, yPx: topPx })
      : { x: 0, y: 0, width: 0, height: 0 };
  const relativeEndRect = endRect
    ? convertRectPosToNewOriginPos(endRect, { xPx: leftPx, yPx: topPx })
    : { x: 0, y: 0, width: 0, height: 0 };

  const [defaultStartSide, defaultEndSide] = generateDefaultStartEndSides(firstPos, lastPos);
  const currentStartPort = !!realTimeStartPort ? realTimeStartPort : startPort;
  const currentEndPort = !!realTimeEndPort ? realTimeEndPort : endPort;

  // Determine the start widget based on the start port. If the start port exists, fetch the corresponding widget element.
  const realTimeStartWidgetElement = !!currentStartPort ? getElementForWidgetId(currentStartPort.widgetId) : null;

  // Calculate the rotation of the start widget. If the start widget exists, get its rotation, otherwise default to 0.
  const startWidgetRotation = !!realTimeStartWidgetElement
    ? getWidgetRotation(realTimeStartWidgetElement)
    : startWidgetData?.rotateDeg ?? 0;

  // Determine the actual start side of the line.
  // If startPort is real time updating due to in progress drag or resize, then use that realTimeStartPort.
  // Otherwise use widget data.
  const actualStartSide =
    !!realTimeStartPort?.side && !!startWidgetData
      ? getLineDirectionOfSideWithRotation(realTimeStartPort?.side, startWidgetRotation)
      : !!startPort?.side && !!startWidgetData
      ? getLineDirectionOfSideWithRotation(startPort?.side, startWidgetRotation)
      : defaultStartSide;

  // Determine the end widget in a similar manner to the start widget, based on the end port.
  const realTimeEndWidgetElement = !!currentEndPort ? getElementForWidgetId(currentEndPort.widgetId) : null;

  // Calculate the rotation of the end widget. Default to 0 if the end widget does not exist.
  const endWidgetRotation = !!realTimeEndWidgetElement
    ? getWidgetRotation(realTimeEndWidgetElement)
    : endWidgetData?.rotateDeg ?? 0;

  // Determine the actual end side of the line, using the same logic as for the start side.
  const actualEndSide =
    !!realTimeEndPort?.side && !!endWidgetData
      ? getLineDirectionOfSideWithRotation(realTimeEndPort?.side, endWidgetRotation)
      : !!endPort?.side && !!endWidgetData
      ? getLineDirectionOfSideWithRotation(endPort?.side, endWidgetRotation)
      : defaultEndSide;

  return {
    relativeStartRect,
    relativeEndRect,
    startSide: actualStartSide,
    endSide: actualEndSide,
    startRect,
    endRect,
  };
};

/**
 * Gets widget rect data from store
 * @returns function to get widget rect data from store
 */
export const useGetWidgetDataRect = () => {
  const store = useAppStore();
  const storeState = store.getState() as RootState;

  /**
   * Retrieves the widget data and its corresponding rectangle dimensions based on a given widget ID.
   *
   * @param widgetId - The ID of the widget.
   *
   * @returns An object containing the widget data and its rectangle dimensions.
   */
  const getWidgetBoundingRectData = useCallback(
    (widgetId: string | null) => {
      const widgetData = !!widgetId ? selectWidget(widgetId)(storeState) : null;
      const widgetRectData = !!widgetData ? generateBoundingRectFromWidget(widgetData) : null;
      return {
        widgetData,
        widgetRectData,
      };
    },
    [storeState],
  );

  /**
   * Retrieves the group widget data and its group ID based on a member widget ID.
   *
   * @param widgetId - The ID of the member widget within the group.
   *
   * @returns A tuple containing the group widget data and its group ID.
   */
  const getWidgetDataOfGroupFromWidgetId = useCallback(
    (widgetId: string | null): [GroupWidgetData, string] => {
      const result = !!widgetId ? selectGroupWidgetFromMemberWidgetId(widgetId)(storeState) : [null, ''];
      const [widgetData, groupId] = result as [GroupWidgetData, string];
      return [widgetData, groupId];
    },
    [storeState],
  );

  /**
   * Retrieves the group widget data based on a given group ID.
   *
   * @param groupId - The ID of the widget group.
   *
   * @returns The group widget data corresponding to the given group ID.
   */
  const getWidgetDataOfGroup = useCallback(
    (groupId: string | null): GroupWidgetData | null => {
      const result = !!groupId ? selectWidget(groupId)(storeState) : null;
      return result as GroupWidgetData;
    },
    [storeState],
  );

  /**
   * Calculates the rectangle dimensions of a widget group based on a member widget ID.
   *
   * @param widgetId - The ID of the member widget within the group.
   *
   * @returns An object containing the rectangle dimensions of the widget group.
   */
  const getRectOfGroupFromWidgetId = useCallback(
    (widgetId: string | null): WrapperPos | null => {
      const groupWidgetData = getWidgetDataOfGroup(widgetId);
      // go through each widget in the group and get the rect for each. then look at the smallest x, y for toppx and leftpx and
      // calculate the total width and height
      const memberWidgetIds = groupWidgetData?.memberWidgetIds;
      if (!memberWidgetIds) return null;
      let smallestTopPx = Infinity;
      let smallestLeftPx = Infinity;
      let largestBottomPx = -Infinity;
      let largestRightPx = -Infinity;

      memberWidgetIds.forEach((memberWidgetId) => {
        const { widgetRectData, widgetData } = getWidgetBoundingRectData(memberWidgetId);
        if (!widgetRectData || !widgetData) return;
        const { x, y, width, height } = widgetRectData;
        if (x < smallestLeftPx) smallestLeftPx = x;
        if (y < smallestTopPx) smallestTopPx = y;
        if (x + width > largestRightPx) largestRightPx = x + width;
        if (y + height > largestBottomPx) largestBottomPx = y + height;
      });
      return {
        leftPx: smallestLeftPx,
        topPx: smallestTopPx,
        widthPx: largestRightPx - smallestLeftPx,
        heightPx: largestBottomPx - smallestTopPx,
      };
    },
    [getWidgetDataOfGroup, getWidgetBoundingRectData],
  );

  /**
   * Retrieves the rectangle dimensions of a widget group in the form of a Rect object, based on a member widget ID.
   *
   * @param widgetId - The ID of the member widget within the group.
   *
   * @returns A Rect object containing the rectangle dimensions of the widget group.
   */
  const getRectOfGroupFromWidgetIdAsRect = useCallback(
    (widgetId: string | null): Rect => {
      const wrapperPos = getRectOfGroupFromWidgetId(widgetId);
      if (!wrapperPos) return { x: 0, y: 0, width: 0, height: 0 };
      return convertWrapperPosToRect(wrapperPos);
    },
    [getRectOfGroupFromWidgetId],
  );

  /**
   * Retrieves the widget data for all groups.
   *
   * @returns An array containing the widget data for all groups.
   */
  const getWidgetDataOfAllGroups = useCallback(() => {
    const allGroupWidgets = selectGroupWidgets(storeState);
    return allGroupWidgets;
  }, [storeState]);

  return {
    getWidgetBoundingRectData,
    getWidgetDataOfGroupFromWidgetId,
    getWidgetDataOfGroup,
    getWidgetDataOfAllGroups,
    getRectOfGroupFromWidgetId,
    getRectOfGroupFromWidgetIdAsRect,
  };
};
